import {
    ChevronDown,
    Gauge,
    ShieldX,
    SlidersHorizontal,
    Target,
    Zap,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
    findMarket,
    useTokenPairDetail,
    useTokenPairs,
    useTokenPairStore,
} from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import { useExecutionDisplay } from '../hooks/useExecutionDisplay';
import { useTriggerPriceMode } from '../hooks/useTriggerPriceMode';
import { useTradingStore } from '../store';
import { getLeverageScale, getNearestLeverageOption } from '../utils';
import { getMaintenanceRate } from '../utils/maintenanceRate';
import {
    liquidatesFirstStopLossHint,
    longOnlyTakeProfitHint,
} from '../utils/triggerSetting';

import { LeverageSlider } from './LeverageSlider';
import { LimitPriceField, OrderTypeTabs } from './LimitOrderControls';

/** The sentinel the toggle group uses for "I'll type a price". */
const PRICE_MODE = 'price';

interface TriggerControlProps {
    label: string;
    icon: React.ReactNode;
    options: readonly string[];
    /** store value: 'Off', an ROI preset like '100%', or a bare price */
    value: string;
    onChange: (next: string) => void;
    /** live mark, shown as the placeholder so a typed price has a reference */
    markPrice: number;
    disabled?: boolean;
    compact?: boolean;
    /** caption under the presets, e.g. which ones a short cannot reach */
    hint?: string | null;
}

/**
 * A TP or SL control: the ROI presets, plus a `$` mode for an absolute price.
 *
 * Reported by aRtik — the presets are ROI on collateral, which answers "how much
 * am I risking" but cannot say "get me out at 80,435". The post-open editor has
 * always taken a price; this brings the order form level with it.
 *
 * Price mode is local UI state so the field can sit empty while the user types.
 * Nothing is armed until the number parses, which is why entering the mode
 * clears the trigger rather than carrying the old preset silently underneath a
 * blank box.
 */
const TriggerControl: React.FC<TriggerControlProps> = ({
    label,
    icon,
    options,
    value,
    onChange,
    markPrice,
    disabled,
    compact,
    hint,
}) => {
    const { priceMode, draft, enterPriceMode, choosePreset, setDraft } =
        useTriggerPriceMode(value, onChange);

    return (
        <div>
            <SectionLabel icon={icon} label={label} compact={compact} />
            <ToggleGroup
                type="single"
                value={priceMode ? PRICE_MODE : value}
                onValueChange={(next) => {
                    if (!next) return;
                    // nothing is armed until a price is typed
                    if (next === PRICE_MODE) enterPriceMode();
                    else choosePreset(next);
                }}
                className="w-full"
                disabled={disabled}
            >
                {options.map((opt) => (
                    <ToggleGroupItem key={opt} value={opt} className="flex-1">
                        {opt}
                    </ToggleGroupItem>
                ))}
                <ToggleGroupItem
                    value={PRICE_MODE}
                    className="flex-1"
                    aria-label={`${label} at a price`}
                >
                    $
                </ToggleGroupItem>
            </ToggleGroup>
            {priceMode && (
                <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min={0}
                    autoFocus
                    disabled={disabled}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    aria-label={`${label} price`}
                    placeholder={
                        markPrice > 0
                            ? `${label} price (mark ${markPrice})`
                            : `${label} price`
                    }
                    className="mt-1.5 h-9 w-full rounded-lg border border-border bg-input px-2.5 font-mono text-sm tabular-nums text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50"
                />
            )}
            {hint && (
                <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>
            )}
        </div>
    );
};

const TAKE_PROFIT_OPTIONS = ['Off', '100%', '200%', '300%'] as const;
const STOP_LOSS_OPTIONS = ['Off', '25%', '50%', '75%'] as const;
const SLIPPAGE_OPTIONS = [
    { label: '0.5%', bps: 50 },
    { label: '1%', bps: 100 },
    { label: '2%', bps: 200 },
] as const;

// Desktop: TP/SL/slippage collapse behind a summary row (Pandora gear
// pattern). The armed values never leave the screen — the summary IS the
// printed commitment; only the selectors hide.
const CONFIG_EXPANDED_KEY = 'snappy.configExpanded.v1';

function loadConfigExpanded(): boolean {
    try {
        return localStorage.getItem(CONFIG_EXPANDED_KEY) === '1';
    } catch {
        return false;
    }
}

interface TradeConfigTogglesProps {
    compact?: boolean;
}

interface SectionLabelProps {
    icon: React.ReactNode;
    label: string;
    compact?: boolean;
}

const SectionLabel: React.FC<SectionLabelProps> = ({ icon, label, compact }) =>
    compact ? (
        <div className="flex items-center gap-2 text-muted-foreground">
            {icon}
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em]">
                {label}
            </span>
        </div>
    ) : (
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {label}
        </span>
    );

const TradeConfigToggles: React.FC<TradeConfigTogglesProps> = React.memo(
    ({ compact }) => {
        const networkId = useTokenPairStore((s) => s.networkId);
        const selectedPair = useTokenPairStore((s) => s.selectedPair);
        const leverage = useTradingStore((s) => s.leverage);
        const setLeverage = useTradingStore((s) => s.setLeverage);
        const leverageAuto = useTradingStore((s) => s.leverageAuto);
        const chooseLeverage = useTradingStore((s) => s.chooseLeverage);
        const takeProfit = useTradingStore((s) => s.takeProfit);
        const setTakeProfit = useTradingStore((s) => s.setTakeProfit);
        const stopLoss = useTradingStore((s) => s.stopLoss);
        const setStopLoss = useTradingStore((s) => s.setStopLoss);
        const slippageBps = useTradingStore((s) => s.slippageBps);
        // the live mark, so a typed trigger price has a reference to aim at
        const markPrice = useTradingStore((s) => s.currentValue);
        const setSlippageBps = useTradingStore((s) => s.setSlippageBps);
        const orderType = useTradingStore((s) => s.orderType);
        const setOrderType = useTradingStore((s) => s.setOrderType);
        const limitPriceDraft = useTradingStore((s) => s.limitPrice);
        const setLimitPrice = useTradingStore((s) => s.setLimitPrice);
        const { executionInFlight } = useExecutionDisplay();
        const { data: pairDetail } = useTokenPairDetail(
            networkId,
            selectedPair,
        );

        // The markets list is the more reliable source (it powers the whole
        // sidebar); the per-pair detail refines the ladder when available.
        const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
        const { data: marketsData } = useTokenPairs(networkId);
        const marketMaxLeverage = useMemo(
            () =>
                findMarket(marketsData?.markets, selectedCoin)?.maxLeverage ??
                null,
            [marketsData, selectedCoin],
        );

        // The market's margin terms, at the tier this order's notional lands
        // in — the same read `SpeedTradeRail` uses for its liquidation
        // estimate. It decides whether a stop-loss preset sits in front of
        // liquidation or behind it.
        const betAmount = useTradingStore((s) => s.betAmount);
        const maintenanceRate = useMemo(
            () =>
                getMaintenanceRate(
                    pairDetail?.leverage,
                    marketMaxLeverage,
                    betAmount > 0 ? betAmount * leverage : null,
                ),
            [pairDetail?.leverage, marketMaxLeverage, betAmount, leverage],
        );

        const leverageScale = useMemo(() => {
            const scale = getLeverageScale(pairDetail?.leverage);
            if (scale.length > 0) return scale;
            // Detail endpoint unavailable — degrade to integer steps from
            // the markets list's maxLeverage. Floor at 2×: some pairs set a
            // fractional min (e.g. 1.1×) that 1× would undershoot.
            if (marketMaxLeverage !== null && marketMaxLeverage >= 2) {
                return Array.from(
                    { length: Math.floor(marketMaxLeverage) - 1 },
                    (_, i) => i + 2,
                );
            }
            return [];
        }, [pairDetail?.leverage, marketMaxLeverage]);

        // Keep leverage on the pair's real ladder. Auto mode (no explicit
        // user pick yet) rides each market's MAX; once the user picks, we
        // clamp their choice to the nearest allowed value on switches
        // (e.g. BTC @30× → ATOM, which caps at 5×).
        useEffect(() => {
            if (leverageScale.length === 0) return;
            const nextLeverage = leverageAuto
                ? leverageScale[leverageScale.length - 1]
                : getNearestLeverageOption(leverageScale, leverage);
            if (nextLeverage !== leverage) {
                setLeverage(nextLeverage);
            }
        }, [leverageScale, leverage, leverageAuto, setLeverage]);

        const disabled = !compact && executionInFlight;
        const sectionGap = compact ? 'space-y-2' : 'mb-2';
        const lastSectionGap = compact ? 'space-y-2' : 'mb-3';

        // TP/SL/slippage collapse behind the summary row on BOTH surfaces
        // (per Alex 2026-07-11); the expansion preference is shared.
        const [configExpanded, setConfigExpanded] =
            useState(loadConfigExpanded);
        const showConfig = configExpanded;
        const toggleConfig = () => {
            setConfigExpanded((prev) => {
                const next = !prev;
                try {
                    localStorage.setItem(CONFIG_EXPANDED_KEY, next ? '1' : '0');
                } catch {
                    // preference just won't persist
                }
                return next;
            });
        };

        const slippageLabel =
            SLIPPAGE_OPTIONS.find((o) => o.bps === slippageBps)?.label ??
            `${slippageBps / 100}%`;

        return (
            <>
                {/* Leverage — slider snapped to the pair's real ladder */}
                <div className={sectionGap}>
                    <SectionLabel
                        icon={<Zap size={12} />}
                        label="Leverage"
                        compact={compact}
                    />
                    <LeverageSlider
                        scale={leverageScale}
                        value={leverage}
                        onCommit={chooseLeverage}
                        disabled={disabled}
                    />
                </div>

                {/* Order type — same placement as the desktop rail: under the
                    leverage slider, above the armed-policy summary. */}
                <div className={sectionGap}>
                    <SectionLabel
                        icon={<Zap size={12} />}
                        label="Order type"
                        compact={compact}
                    />
                    <OrderTypeTabs
                        value={orderType}
                        onChange={setOrderType}
                        disabled={disabled}
                    />
                </div>

                {orderType === 'limit' && (
                    <div className={sectionGap}>
                        <LimitPriceField
                            value={limitPriceDraft}
                            onChange={setLimitPrice}
                            markPrice={markPrice}
                            // the drawer has no price formatter to hand; the raw
                            // mark is what the other fields here show too.
                            formatPrice={(value) => String(value)}
                            disabled={disabled}
                            compact={compact}
                        />
                    </div>
                )}

                {/* Collapsed summary — the armed policy never leaves the
                    screen; only the selectors hide */}
                <button
                    type="button"
                    onClick={toggleConfig}
                    aria-expanded={configExpanded}
                    className={cn(
                        'flex w-full cursor-pointer items-center gap-2 rounded-lg border border-border/60 bg-secondary/30 px-2.5 py-1.5 text-left transition-colors hover:bg-secondary/60',
                        compact ? 'py-2' : 'mb-2',
                    )}
                >
                    <SlidersHorizontal
                        size={11}
                        className="shrink-0 text-muted-foreground"
                    />
                    <span className="min-w-0 flex-1 truncate font-mono text-[10px] tabular-nums text-muted-foreground">
                        TP{' '}
                        <span
                            className={cn(
                                'font-bold',
                                takeProfit !== 'Off'
                                    ? 'text-primary'
                                    : 'text-foreground',
                            )}
                        >
                            {takeProfit}
                        </span>
                        {' · '}SL{' '}
                        <span className="font-bold text-foreground">
                            {stopLoss}
                        </span>
                        {' · '}Slip{' '}
                        <span className="font-bold text-foreground">
                            {slippageLabel}
                        </span>
                    </span>
                    <ChevronDown
                        size={12}
                        className={cn(
                            'shrink-0 text-muted-foreground transition-transform duration-200',
                            configExpanded && 'rotate-180',
                        )}
                    />
                </button>

                {showConfig && (
                    <>
                        {/* Take profit: ROI presets, or an absolute price */}
                        <div className={sectionGap}>
                            <TriggerControl
                                label="Take profit"
                                icon={<Target size={12} />}
                                options={TAKE_PROFIT_OPTIONS}
                                value={takeProfit}
                                onChange={setTakeProfit}
                                markPrice={markPrice}
                                disabled={disabled}
                                compact={compact}
                                hint={longOnlyTakeProfitHint(
                                    TAKE_PROFIT_OPTIONS,
                                    leverage,
                                )}
                            />
                        </div>

                        {/* Stop loss: same, on the losing side */}
                        <div className={sectionGap}>
                            <TriggerControl
                                label="Stop loss"
                                icon={<ShieldX size={12} />}
                                options={STOP_LOSS_OPTIONS}
                                value={stopLoss}
                                onChange={setStopLoss}
                                markPrice={markPrice}
                                disabled={disabled}
                                compact={compact}
                                hint={liquidatesFirstStopLossHint(
                                    STOP_LOSS_OPTIONS,
                                    leverage,
                                    maintenanceRate,
                                )}
                            />
                        </div>

                        {/* Slippage Toggle */}
                        <div className={lastSectionGap}>
                            <SectionLabel
                                icon={<Gauge size={12} />}
                                label="Slippage"
                                compact={compact}
                            />
                            <ToggleGroup
                                type="single"
                                value={slippageBps.toString()}
                                onValueChange={(value) => {
                                    if (!value) return;
                                    const bps = Number(value);
                                    if (Number.isFinite(bps))
                                        setSlippageBps(bps);
                                }}
                                className="w-full"
                                disabled={disabled}
                            >
                                {SLIPPAGE_OPTIONS.map((opt) => (
                                    <ToggleGroupItem
                                        key={opt.bps}
                                        value={opt.bps.toString()}
                                        className="flex-1"
                                    >
                                        {opt.label}
                                    </ToggleGroupItem>
                                ))}
                            </ToggleGroup>
                        </div>
                    </>
                )}
            </>
        );
    },
);

TradeConfigToggles.displayName = 'TradeConfigToggles';

export default TradeConfigToggles;
